import * as React from 'react';
import {faAngleRight} from '@fortawesome/pro-solid-svg-icons';
import {faSearchPlus} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useState} from 'react';

import Button from '../../ui/Buttons/Button';
import ImpressionTracker from '../../analytics/impression-tracker/ImpressionTracker';
import {Badges} from '../../items/badges/Badges';
import {GaItemListName} from '../../ga/ga.types';
import {ImageZoomModal} from '../../items/image-zoom-modal/ImageZoomModal';
import {ItemToAdd} from '../../order-items/order-items.class';
import {Item} from '../../items/item.class';
import {OrderItemsWorkflow} from '../../../client/order-items/order-items.workflow';
import {OrdersService} from '../../../client/orders/orders.service';
import {Price} from '../../pricing/Price';
import {SignalsService} from '../../../client/search/signals/signals.service';
import {Stepper} from '../../ui/forms/Stepper/Stepper';
import {StockStatus} from '../../items/stock-status/StockStatus';
import {User} from '../../users/user.class';
import {useService} from '../../react/ServiceContext';

interface FeaturedProductProps {
    featuredProduct: Item;
    fusionQueryId: string;
    requestID: string;
    user: User;
}

export const FeaturedProduct = ({featuredProduct, fusionQueryId, requestID, user}: FeaturedProductProps) => {
    const [orderQty, setOrderQty] = useState(1);
    const [showImageZoom, setShowImageZoom] = useState(false);
    const componentName = `featured-product-component`;
    const gaItemListName: GaItemListName = `Google Shopping Landing Page`;
    const orderItemsWorkflow: OrderItemsWorkflow = useService(`orderItemsWorkflow`);
    const ordersService: OrdersService = useService(`ordersService`);
    const signalsService: SignalsService = useService(`signalsService`);

    /**
     * TBD
     */
    const itemClickEvents = () => {
        signalsService
            .sendSignals([
                {
                    request_id_s: requestID,
                    type: `click`,
                    params: {
                        collection: `ImperialItems`,
                        docId: featuredProduct.id,
                        fusion_query_id: fusionQueryId,
                        referrer: location.href,
                        res_pos: 1,
                        source: gaItemListName,
                        url: `/item/${featuredProduct.id}`,
                    },
                },
            ])
            .catch((sendSignalsErr) => {
                // fail silently, most likely a navigation before signals finish saving
                // eslint-disable-next-line no-console
                console.log(`Error sending fusion signals`, sendSignalsErr);
            });
    };

    /**
     * Adds items to order
     * @param itemsToAdd - Items to add to the order
     * @private
     */
    const _addToOrder = (itemsToAdd: ItemToAdd[]) => {
        orderItemsWorkflow.addToOrderModal(itemsToAdd, componentName, true);

        // Record analytics
        signalsService
            .sendSignals([
                {
                    params: {
                        collection: `ImperialItems`,
                        docId: featuredProduct.id,
                        fusion_query_id: fusionQueryId,
                        referrer: location.href,
                        res_pos: 1,
                        source: gaItemListName,
                    },
                    request_id_s: requestID,
                    type: `add_to_cart`,
                },
            ])
            .catch((sendSignalsErr) => {
                // fail silently, most likely a navigation before signals finish saving
                // eslint-disable-next-line no-console
                console.log(`Error sending fusion signals`, sendSignalsErr);
            });
    };

    /**
     * Template
     */
    return (
        <div className="tw-relative tw-flex tw-flex-col tw-border tw-rounded-sm tw-bg-white tw-border-gray-700 tw-mb-4">
            <div className="tw-p-3 tw-bg-gray-600 tw-text-white">
                <h5 className="tw-mb-0">Featured</h5>
            </div>
            <div className="tw-p-4">
                <div className="tw-flex">
                    <div className="tw-flex-shrink-0 tw-mr-3 tw-hidden md:tw-flex tw-mt-5">
                        <a
                            aria-label="Zoom Image"
                            className="tw-cursor-pointer"
                            onClick={() => {
                                setShowImageZoom(true);
                            }}
                        >
                            <FontAwesomeIcon
                                className="tw-text-gray-400 tw-p-2 tw-mt-1"
                                icon={faSearchPlus}
                                size="2x"
                            />
                        </a>
                        <div>
                            <img
                                alt="Featured Product Image"
                                className="tw-max-w-full tw-h-auto"
                                height="100"
                                src={`/ProductImageThumbs120${featuredProduct.imagePath}`}
                                width="100"
                            />
                        </div>
                    </div>
                    <div className="tw-flex tw-flex-col lg:tw-flex-row tw-w-full">
                        <div className="tw-w-full lg:tw-w-1/2">
                            <div className="tw-mb-2 tw-font-bold">
                                <div>
                                    <Badges item={featuredProduct} />
                                </div>
                            </div>
                            <div className="tw-text-sm tw-font-normal tw-leading-[1.2]">
                                <ImpressionTracker
                                    as="span"
                                    data={{
                                        dimension16: ordersService?.currentOrderNumber,
                                        index: 1,
                                        item_id: featuredProduct.id,
                                        item_list_name: gaItemListName,
                                    }}
                                    trackImpressions={false}
                                >
                                    #
                                    <a
                                        className="tw-no-underline tw-text-inherit"
                                        href={`/item/${featuredProduct.id}`}
                                        onClick={() => {
                                            itemClickEvents();
                                        }}
                                        role="button"
                                    >
                                        {featuredProduct.prettyid}
                                    </a>
                                </ImpressionTracker>
                            </div>
                            <div className="tw-mb-3">{featuredProduct.h1SeoOverride || featuredProduct.h1HTML}</div>
                            <div className="tw-flex md:tw-hidden">
                                <a
                                    aria-label="Zoom Image"
                                    className="tw-cursor-pointer"
                                    onClick={() => {
                                        setShowImageZoom(true);
                                    }}
                                >
                                    <FontAwesomeIcon
                                        className="tw-text-gray-400 tw-p-2 tw-mt-1"
                                        icon={faSearchPlus}
                                        size="2x"
                                    />
                                </a>
                                <div>
                                    <img
                                        alt="Featured Product Image"
                                        className="tw-max-w-full tw-h-auto"
                                        height="100"
                                        src={`/ProductImageThumbs120${featuredProduct.imagePath}`}
                                        width="100"
                                    />
                                </div>
                            </div>
                            <div className="tw-text-right md:tw-text-left">
                                <div>Pkg Qty: {featuredProduct.pkgQty}</div>
                                {!(
                                    (featuredProduct.inStock === false || featuredProduct.itemtype === 'D') &&
                                    featuredProduct.isdiscontinue
                                ) && (
                                    <Price
                                        alias="SearchResultItem"
                                        className=" tw-text-xl tw-mb-2 tw-leading-[1.2] tw-mt-1 !tw-font-bold"
                                        doNotShowEachPrice={true}
                                        item={featuredProduct}
                                        user={user}
                                    />
                                )}
                            </div>
                            {!featuredProduct.isdiscontinue && (
                                <div className="tw-mb-3">
                                    <StockStatus
                                        item={featuredProduct}
                                        unitsOrd={orderQty}
                                        user={user}
                                    />
                                </div>
                            )}
                            {featuredProduct.itemQtyLimit_i && (
                                <div className="tw-mb-3">
                                    Maximum order quantity: {Math.floor(featuredProduct.itemQtyLimit_i / featuredProduct.pkgQty)}
                                </div>
                            )}
                            <div className="tw-flex tw-flex-row justify-content-between">
                                <div className="tw-w-[130px]">
                                    <Stepper
                                        className="[&_button]:!tw-h-[52px] [&_input]:!tw-h-[52px] [&_button]:!tw-w-[52px] [&_input]:!tw-w-[52px]"
                                        disabled={featuredProduct.isdiscontinue}
                                        maximum={featuredProduct.itemQtyLimit_i}
                                        onUpdate={(qty) => {
                                            setOrderQty(qty);
                                        }}
                                        onUpdateAllowInvalid={false}
                                        value={orderQty}
                                    />
                                </div>
                                <Button
                                    disabled={featuredProduct.isdiscontinue}
                                    onClick={() => {
                                        _addToOrder([
                                            {
                                                gaEcommerce: {item_list_name: gaItemListName},
                                                item: featuredProduct.id,
                                                unitsOrdered: orderQty,
                                            },
                                        ]);
                                    }}
                                    size="lg"
                                    type="button"
                                    variant="primary"
                                >
                                    Add to Order
                                </Button>
                            </div>
                        </div>
                        <div className="tw-w-full lg:tw-w-1/2 tw-ml-8">
                            {featuredProduct.techSpecs && (
                                <ul className="tw-mb-2">
                                    {featuredProduct.techSpecs.map((spec, index) => {
                                        if (index > 2) {
                                            return null;
                                        }
                                        return (
                                            <li key={index}>
                                                {spec.facetName}:<span className="tw-ml-2">{spec.facetValue}</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                            <div className="tw-mb-2">
                                <ImpressionTracker
                                    as="span"
                                    data={{
                                        dimension16: ordersService?.currentOrderNumber,
                                        index: 1,
                                        item_id: featuredProduct.id,
                                        item_list_name: gaItemListName,
                                    }}
                                >
                                    <a
                                        href={`/item/${featuredProduct.id}`}
                                        onClick={() => {
                                            itemClickEvents();
                                        }}
                                    >
                                        View full product details{' '}
                                        <FontAwesomeIcon
                                            className="tw-text-blue-200"
                                            icon={faAngleRight}
                                            size="2x"
                                        />
                                    </a>
                                </ImpressionTracker>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ImageZoomModal
                activeIndex={0}
                item={featuredProduct}
                onHide={() => {
                    setShowImageZoom(false);
                }}
                show={showImageZoom}
            />
        </div>
    );
};
